import { createContext, useContext, useState, useRef, useMemo, useCallback, memo } from "react"
import type { SelectionContextProps, CurrentSelection } from "./SelectionContext.types"
import { Checkbox } from "../shadcn/components/ui/checkbox"

const SelectionContext = createContext<SelectionContextProps>({ currentSelection: {} })

export const useSelectionContext = () => useContext(SelectionContext)

export const SelectionContextProvider = ({ ids, children }: { ids: string[]; children: React.ReactNode }) => {
	const [currentSelection, setCurrentSelectionBase] = useState<CurrentSelection>({})
	const currentSelectionRef = useRef<CurrentSelection>({})

	const setCurrentSelection = useCallback((newSelection) => {
		setCurrentSelectionBase(newSelection)
		currentSelectionRef.current = newSelection
	}, [])

	useMemo(() => {
		setCurrentSelection(
			Object.entries(currentSelectionRef.current).reduce((selectionOut, [id, isSelected]) => {
				if (ids.includes(id)) {
					selectionOut[id] = isSelected
				}
				return selectionOut
			}, {})
		)
	}, [ids, setCurrentSelection])

	const changeSelectionForId = useCallback(
		(id) => {
			currentSelectionRef.current[id] = !currentSelectionRef.current[id]
			if (id === "all") {
				for (const thisId of ids) {
					currentSelectionRef.current[thisId] = currentSelectionRef.current[id]
				}
			} else if (!currentSelectionRef.current[id]) {
				currentSelectionRef.current.all = false
			}
			setCurrentSelection({ ...currentSelectionRef.current })
		},
		[ids, setCurrentSelection]
	)

	const selectedCount = useMemo(
		() => Object.values(currentSelection).reduce((a, b) => a + Number(b), 0) - (currentSelection.all ? 1 : 0),
		[currentSelection]
	)

	const clearSelection = useCallback(() => {
		setCurrentSelection({})
	}, [setCurrentSelection])

	const selectedIds = useMemo(() => getSelectedIds(currentSelection), [currentSelection])

	return (
		<SelectionContext.Provider
			value={{ selectedIds, currentSelection, changeSelectionForId, selectedCount, clearSelection }}>
			{children}
		</SelectionContext.Provider>
	)
}

function getSelectedIds(currentSelection) {
	return Object.entries(currentSelection ?? {})
		.map(([id, selected]) => (selected ? id : null))
		.filter((id) => id !== "all" && id != null)
}

export const SelectorInput = memo(
	({
		rowId,
		isSelected,
		changeSelectionForId,
	}: {
		rowId: string
		isSelected: boolean
		changeSelectionForId: (id: string) => void
	}) => (
		<Checkbox
			className="clickable"
			tabIndex={0}
			id={`table-row-selection-checkbox-${rowId}`}
			checked={!!isSelected}
			onCheckedChange={() => {
				changeSelectionForId(rowId)
			}}
			onClick={(e) => {
				e.stopPropagation()
			}}
		/>
	)
)

function* batchArray(array, batchSize) {
	const arrayCopy = [...array]
	while (arrayCopy.length > 0) {
		yield arrayCopy.splice(0, batchSize)
	}
}

const BATCH_SIZE = 10

export const useBatchProcessing = () => {
	const [isRunning, setIsRunning] = useState(false)
	const runInBatches = useCallback(
		async (func: (id) => Promise<void>, ids: Array<number | string>, batchSize: number = BATCH_SIZE) => {
			if (isRunning) {
				console.log("already running batch processing")
				return
			}
			setIsRunning(true)
			const failures: { [key: string]: string } = {}
			for (const batch of batchArray(ids, batchSize ?? BATCH_SIZE)) {
				await Promise.all(
					batch.map((id) =>
						func(id).catch((e) => {
							failures[id] = e.message ?? e.toString()
						})
					)
				)
			}
			setIsRunning(false)
			return failures
		},
		[isRunning]
	)

	return [isRunning, runInBatches] as const
}
